// ダッシュボード（design/README.md §1）。KPI4枚・進行状況・要対応・アフィリ経由売上。
import { useEffect, useMemo, useState } from "react";
import type { GfActionKey, GfAsset, GfEntry, GfTabProps, Loadable } from "../../../types/gifting";
import { fetchGfAssets } from "../../../api/giftingClient";
import {
  ACTION_META,
  deadlineInfo,
  fmtAsOf,
  fmtMD,
  fmtYen,
  fmtYenShort,
  fmtYmd,
  igDetectCounts,
  igDetectTodoOf,
  isArchived,
  listCandidates,
  nextActionsOf,
  STAGE_NAMES,
  tierClass,
  typeHasAffiliate,
  typeHasFee,
  effectiveType,
} from "../../../lib/giftingRules";
import { GfCard, GfErrorBanner, GfHBar, GfNotConnected, GfSkeleton, GfTag, type GfTone } from "../ui/GfUi";
import { creatorMap, errMsg } from "./kit/helpers";
import { useGfIgDetectedMedia } from "../useGfIgDetect";
import { useGfShipStatus } from "../useGfShipStatus";
import { agoLabel, calDaysAgo } from "../panels/entry/dayLabel";

interface Todo {
  key: string;
  title: string;
  sub: string;
  tag: string;
  tone: GfTone;
  onClick: () => void;
}

const TONE_ORDER: Record<string, number> = { warn: 0, caution: 1, info: 2 };
const MAX_TODOS = 8;

/** 大カード（上段2×2）。ship+fix は「住所入力済」1枚にまとめる */
const BIG_CARDS: ReadonlyArray<{ id: string; title: string; keys: readonly GfActionKey[]; desc: string; tier: 0 | 1 | 2 | 3 }> = [
  { id: "reply", title: "返信あり", keys: ["reply"], desc: ACTION_META.reply.desc, tier: 0 },
  { id: "ship", title: "住所入力済", keys: ["ship", "fix"], desc: "今すぐ出荷指示を。書式エラーは修正してから", tier: 1 },
  { id: "remind", title: "リマインド", keys: ["remind"], desc: ACTION_META.remind.desc, tier: 3 },
  { id: "postdue", title: "投稿期限超過", keys: ["postdue"], desc: ACTION_META.postdue.desc, tier: 2 },
];
/** 小カード（下段） */
const SMALL_CARDS: ReadonlyArray<{ key: GfActionKey; title: string }> = [
  { key: "nudge", title: "催促（返信待ち4日〜）" },
  { key: "addrnudge", title: "住所を催促" },
  { key: "confirm", title: "投稿を確認" },
  { key: "terms", title: "条件を確定" },
  { key: "pitch", title: "打診する" },
  { key: "license", title: "許諾を記録" },
];

interface DashRow {
  id: string;
  key: GfActionKey;
  name: string;
  camp: string;
  detail: string;
}

function fmtToday(nowIso: string): string {
  const d = new Date(nowIso);
  return new Intl.DateTimeFormat("ja-JP", { timeZone: "Asia/Tokyo", month: "long", day: "numeric", weekday: "short" })
    .format(Number.isNaN(d.getTime()) ? new Date() : d)
    .replace(/\((.)\)/, "（$1）");
}

/** 大カードの行の右側「受信 昨日」「残り13日」「入力済 · 本人フォーム」「書式エラー」「超過3日」 */
function rowDetail(e: GfEntry, key: GfActionKey, nowIso: string): string {
  const dl = deadlineInfo(e, nowIso);
  switch (key) {
    case "reply":
      return `受信 ${agoLabel(calDaysAgo(e.last_in_at, nowIso))}`;
    case "fix":
      return "書式エラー";
    case "ship":
      return `入力済 · ${e.address_source === "proxy" ? "代理入力" : "本人フォーム"}`;
    case "remind":
      return dl ? `残り${dl.daysLeft}日` : "リマインド";
    case "postdue":
      return dl ? `超過${-dl.daysLeft}日` : "期限超過";
    case "nudge":
      return "返信待ち";
    case "addrnudge":
      return "入力待ち";
    default:
      return "";
  }
}

export function DashboardTab({ data, nowIso, month, nav, refresh }: GfTabProps) {
  const { board, monthly, payments } = data;
  const campaignsById = useMemo(() => new Map(board.campaigns.map((c) => [c.id, c])), [board.campaigns]);
  const creators = useMemo(() => creatorMap(board.creators), [board.creators]);

  // 素材の期限間近は board に載っていないので別取得（失敗しても他の箇所は出す）
  const [assets, setAssets] = useState<Loadable<GfAsset[]>>({ status: "loading" });
  useEffect(() => {
    let live = true;
    fetchGfAssets("expiring")
      .then((d) => live && setAssets({ status: "ok", data: d }))
      .catch((e: unknown) => live && setAssets({ status: "error", message: errMsg(e) }));
    return () => {
      live = false;
    };
  }, [board]);

  const { status: shipStatus } = useGfShipStatus(board);
  // 第3弾: 検知した投稿（未紐づけ）。取得に失敗しても（旧バックエンドなど）他の要対応は出す
  const { media: detected } = useGfIgDetectedMedia("unlinked", board);
  const detectedList = detected.status === "ok" ? detected.data : [];
  const detectCounts = igDetectCounts(detectedList);

  const live = board.entries.filter((e) => !isArchived(e));

  // ---- KPI ----
  const activeCampaigns = board.campaigns.filter((c) => c.status === "active" && live.some((e) => e.campaign_id === c.id)).length;
  const members = new Set(live.map((e) => e.influencer_id)).size;
  const shippedThisMonth = board.entries.filter(
    (e) => e.status !== "cancelled" && e.shipped_at && fmtYmd(e.shipped_at).slice(0, 7) === month
  ).length;
  const addrWaiting = live.filter((e) => e.stage === 4 && e.status === "waiting_address").length;

  const amazonMonthly = monthly.status === "ok" ? monthly.data.amazon ?? null : null;
  const affKpi =
    monthly.status === "ok"
      ? {
          value: fmtYenShort(monthly.data.shopify.sales + (amazonMonthly?.sales ?? 0)),
          sub: amazonMonthly ? `Shopify ${fmtYenShort(monthly.data.shopify.sales)}＋Amazon ${fmtYenShort(amazonMonthly.sales)}（楽天は未接続）` : "Shopify のみ（楽天・Amazon は未接続）",
        }
      : monthly.status === "loading"
        ? { value: "…", sub: "読み込み中" }
        : { value: "—", sub: "取得できませんでした" };

  const unpaidPersons = payments.status === "ok" ? payments.data.persons.filter((p) => p.unpaid > 0).length : 0;
  const payKpi =
    payments.status === "ok"
      ? { value: fmtYenShort(payments.data.total_unpaid), sub: `${Number(month.slice(5))}月分・未払 ${unpaidPersons}名` }
      : payments.status === "loading"
        ? { value: "…", sub: "読み込み中" }
        : { value: "—", sub: "取得できませんでした" };

  // ---- 進行状況（段階別件数。0=営業リスト） ----
  const candidates = listCandidates(board.creators, board.entries);
  const funnel = STAGE_NAMES.slice(0, 8).map((name, i) => ({
    name,
    count: i === 0 ? candidates.length : live.filter((e) => e.stage === i).length,
  }));
  const funnelMax = Math.max(1, ...funnel.map((f) => f.count));

  // ---- 要対応（v2: nextAction で1人1アクション。一覧のチップと必ず同じ件数） ----
  const actions = useMemo(() => nextActionsOf(live, campaignsById, nowIso), [live, campaignsById, nowIso]);
  const byKey = useMemo(() => {
    const m = new Map<GfActionKey, DashRow[]>();
    const sorted = [...live].sort((x, y) => (actions.get(x.id)?.sortKey ?? 0) - (actions.get(y.id)?.sortKey ?? 0));
    for (const e of sorted) {
      const a = actions.get(e.id);
      if (!a || a.key === "wait") continue;
      const camp = e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null;
      const row: DashRow = {
        id: e.id,
        key: a.key,
        name: creators.get(e.influencer_id)?.name ?? "（不明）",
        camp: camp?.name ?? e.product_name,
        detail: rowDetail(e, a.key, nowIso),
      };
      m.set(a.key, [...(m.get(a.key) ?? []), row]);
    }
    return m;
  }, [live, actions, campaignsById, creators, nowIso]);
  const todayCount = [...byKey.values()].reduce((n, rows) => n + rows.length, 0);
  const goList = (key: GfActionKey, sel: string | null = null) => nav.set({ tab: "deals", view: "list", act: key, sel, creator: null });

  const todos: Todo[] = [];
  // 口座未登録（有償/アフィリ案件で投稿確認以降に進んだクリエイター）
  const payeeSeen = new Set<string>();
  for (const e of board.entries) {
    if (e.stage < 6 || e.status === "cancelled") continue;
    const camp = e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null;
    const et = effectiveType(e, camp);
    const needsPay = typeHasFee(et) || typeHasAffiliate(et);
    const cr = creators.get(e.influencer_id);
    if (!needsPay || !cr || cr.payee_status === "registered" || payeeSeen.has(cr.id)) continue;
    payeeSeen.add(cr.id);
    todos.push({
      key: `p-${cr.id}`,
      title: `${cr.name} の口座が未登録`,
      sub: camp?.name ?? e.product_name,
      tag: "口座",
      tone: "info",
      onClick: () => nav.openCreator(cr.id),
    });
  }
  // 素材の許諾期限
  if (assets.status === "ok") {
    for (const a of assets.data) {
      todos.push({
        key: `a-${a.id}`,
        title: `${a.influencer_name} の素材の許諾期限`,
        sub: a.reuse_until ? `${fmtMD(a.reuse_until)} まで` : "期限間近",
        tag: "素材",
        tone: "caution",
        onClick: () => nav.set({ tab: "assets", asset: "expiring" }),
      });
    }
  }
  // 第2弾: 出荷指示の送信失敗（直近7日）・同期エラー
  if (shipStatus.status === "ok" && (shipStatus.data.failed > 0 || shipStatus.data.last_error)) {
    const s = shipStatus.data;
    todos.push({
      key: "ship-failed",
      title: s.failed > 0 ? `出荷指示の送信失敗 ${s.failed}件` : "追跡番号の同期でエラー",
      sub: s.last_error ? `直近のエラー: ${s.last_error}` : "発送タブで状態を確認して再送してください",
      tag: "出荷",
      tone: "warn",
      onClick: () => nav.set({ tab: "ship" }),
    });
  }
  // 第3弾: 検知した投稿の確認（クリエイター登録済みで案件に紐づいていないものだけ。未登録アカウントは営業候補として件数を添える）
  const detectTodo = igDetectTodoOf(detectedList);
  if (detectTodo) {
    todos.push({
      key: "ig-detect",
      title: detectTodo.title,
      sub: detectTodo.sub,
      tag: "投稿検知",
      tone: "caution",
      onClick: () => nav.set({ tab: "assets" }),
    });
  }
  todos.sort((a, b) => (TONE_ORDER[a.tone] ?? 3) - (TONE_ORDER[b.tone] ?? 3));
  const shownTodos = todos.slice(0, MAX_TODOS);

  const kpis: Array<{ label: string; value: string; sub: string }> = [
    { label: "進行中の案件", value: String(activeCampaigns), sub: `参加クリエイター ${members}名` },
    { label: "今月の発送", value: String(shippedThisMonth), sub: `住所待ち ${addrWaiting}件` },
    { label: "アフィリ経由売上", value: affKpi.value, sub: affKpi.sub },
    { label: "今月の支払予定", value: payKpi.value, sub: payKpi.sub },
  ];

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">ダッシュボード</h1>
        <span className="gf-asof">{fmtAsOf(nowIso)}</span>
      </div>

      <section className="gf-dash2" aria-label="要対応">
        <div className="gf-dash2-head">
          <h2 className="gf-dash2-title">要対応</h2>
          <span className="gf-dash2-sub">
            {fmtToday(nowIso)} · 今日やること {todayCount}件 · 上から順に片付ける
          </span>
        </div>

        <div className="gf-dash2-big">
          {BIG_CARDS.map((c) => {
            const rows = c.keys.flatMap((k) => byKey.get(k) ?? []);
            if (c.keys.length > 1) rows.sort((x, y) => (actions.get(x.id)?.sortKey ?? 0) - (actions.get(y.id)?.sortKey ?? 0));
            const top = rows.slice(0, 3);
            const listKey: GfActionKey = rows[0]?.key ?? c.keys[0];
            return (
              <div
                key={c.id}
                className={`gf-dash2-card ${tierClass(c.tier)}${rows.length === 0 ? " is-zero" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => goList(listKey)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    goList(listKey);
                  }
                }}
              >
                <div className="gf-dash2-card-head">
                  <span className="gf-dash2-card-title">{c.title}</span>
                  <span className="gf-dash2-card-n gf-mono">{rows.length}</span>
                  <span className="gf-dash2-card-desc">{c.desc}</span>
                </div>
                <div className="gf-dash2-rows">
                  {top.map((r) => (
                    <button
                      type="button"
                      key={r.id}
                      className="gf-dash2-row"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        goList(r.key, r.id);
                      }}
                    >
                      <span className="gf-dash2-row-name">{r.name}</span>
                      <span className="gf-dash2-row-camp">{r.camp}</span>
                      <span className="gf-dash2-row-detail">{r.detail}</span>
                    </button>
                  ))}
                  {rows.length === 0 && <div className="gf-dash2-none">いま対応が必要なものはありません</div>}
                </div>
                {rows.length > 0 && (
                  <span className="gf-dash2-more">{rows.length > 3 ? `残り${rows.length - 3}件も一覧で見る →` : "一覧で見る →"}</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="gf-dash2-small">
          {SMALL_CARDS.map((c) => {
            const rows = byKey.get(c.key) ?? [];
            const meta = ACTION_META[c.key];
            return (
              <button
                type="button"
                key={c.key}
                className={`gf-dash2-mini ${tierClass(meta.tier)}${rows.length === 0 ? " is-zero" : ""}`}
                onClick={() => goList(c.key)}
              >
                <span className="gf-dash2-mini-head">
                  <span className="gf-dash2-mini-title">{c.title}</span>
                  <span className="gf-dash2-mini-n gf-mono">{rows.length}</span>
                </span>
                <span className="gf-dash2-mini-names">
                  {rows.length === 0 ? "—" : rows.slice(0, 3).map((r) => r.name).join("・")}
                  {rows.length > 3 ? ` ほか${rows.length - 3}件` : ""}
                </span>
              </button>
            );
          })}
        </div>

        {shownTodos.length > 0 && (
          <div className="gf-dash2-other">
            <div className="gf-dash2-other-title">その他</div>
            {shownTodos.map((t) => (
              <button type="button" className="gf-todo" key={t.key} onClick={t.onClick}>
                <span className="gf-todo-main">
                  <span className="gf-todo-title">{t.title}</span>
                  <span className="gf-todo-sub">{t.sub}</span>
                </span>
                <GfTag tone={t.tone}>{t.tag}</GfTag>
              </button>
            ))}
            {todos.length > shownTodos.length && <div className="gf-note gf-todo-more">ほか {todos.length - shownTodos.length}件（各タブで確認）</div>}
          </div>
        )}
        {assets.status === "error" && <div className="gf-note gf-todo-more">素材の期限は取得できませんでした（{assets.message}）</div>}
        {!detectTodo && detectCounts.sales > 0 && (
          <div className="gf-note gf-todo-more">営業候補: 未登録アカウントのタグ付け投稿 {detectCounts.sales}件（素材庫で確認）</div>
        )}
      </section>

      <div className="gf-grid-kpi">
        {kpis.map((k) => (
          <GfCard key={k.label} className="gf-kpi">
            <span className="gf-kpi-label">{k.label}</span>
            <span className="gf-kpi-value gf-kpi-value-tight">{k.value}</span>
            <span className="gf-kpi-sub">{k.sub}</span>
          </GfCard>
        ))}
      </div>

      <div className="gf-grid-2 is-start">
        <GfCard className="gf-c18 gf-col-card">
          <div className="gf-card-head-row">
            <h2 className="gf-h2">進行状況</h2>
            <button type="button" className="gf-link-btn" onClick={() => nav.set({ tab: "deals" })}>
              パイプラインを開く
            </button>
          </div>
          {funnel.map((f) => (
            <div className="gf-funnel-row" key={f.name}>
              <span className="gf-funnel-name">{f.name}</span>
              <GfHBar pct={(f.count / funnelMax) * 100} />
              <span className="gf-funnel-count">{f.count}</span>
            </div>
          ))}
        </GfCard>

        <GfCard className="gf-c18 gf-col-card">
          <h2 className="gf-h2">アフィリ経由売上（今月）</h2>
          {monthly.status === "loading" && (
            <>
              <GfSkeleton height={14} />
              <GfSkeleton height={8} />
            </>
          )}
          {monthly.status === "error" && <GfErrorBanner message={`アフィリ売上を取得できませんでした（${monthly.message}）`} onRetry={() => void refresh()} />}
          {monthly.status === "ok" && (
            <div className="gf-bar-row">
              <div className="gf-bar-head">
                <span>Shopify</span>
                <span className="gf-mono">{fmtYen(monthly.data.shopify.sales)}</span>
              </div>
              <GfHBar sub pct={monthly.data.shopify.sales > 0 ? (monthly.data.shopify.sales / Math.max(monthly.data.shopify.sales, amazonMonthly?.sales ?? 0)) * 100 : 0} />
              <span className="gf-note">
                成果 {monthly.data.shopify.conversions}件・報酬見込 {fmtYen(monthly.data.shopify.commission)}
              </span>
            </div>
          )}
          {monthly.status === "ok" && amazonMonthly && (
            <div className="gf-bar-row">
              <div className="gf-bar-head">
                <span>Amazon（専用コード経由）</span>
                <span className="gf-mono">{fmtYen(amazonMonthly.sales)}</span>
              </div>
              <GfHBar sub pct={amazonMonthly.sales > 0 ? (amazonMonthly.sales / Math.max(monthly.data.shopify.sales, amazonMonthly.sales)) * 100 : 0} />
              <span className="gf-note">
                成果 {amazonMonthly.conversions}件
                {(amazonMonthly.reward_eligible_count ?? 0) > 0 ? `・報酬見込 ${fmtYen(amazonMonthly.commission)}` : amazonMonthly.conversions > 0 ? "・報酬対象外（表示のみ・料率未設定）" : ""}
                {(amazonMonthly.estimated_count ?? 0) > 0 ? `・按分不可 ${amazonMonthly.estimated_count}件` : ""}
              </span>
            </div>
          )}
          <GfNotConnected title="楽天市場" note="売上計測は未接続です（クリック計測のみ）" />
          {monthly.status === "ok" && !amazonMonthly && <GfNotConnected title="Amazon" note="売上計測は未接続です（クリック計測のみ）" />}
        </GfCard>
      </div>
    </>
  );
}
